import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getCookie, setCookie } from './utils';
import { GoogleLogin } from '@react-oauth/google';
import content from 'content.json';

const brandname = content.brandname;
const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;
const login_api_url = process.env.REACT_APP_LOGIN_API_URL;

function Payment() {
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showGoogleLogin, setShowGoogleLogin] = useState(false);
    const [status, setStatus] = useState(null);
    const [isCheckingPayment, setIsCheckingPayment] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        checkToken();
    }, []);

    const checkToken = () => {
        const cookieValue = getCookie(`${brandname}_token`);
        if (cookieValue) {
            axios.get(`${login_api_url}/api/CheckToken`, {
                headers: {
                    Authorization: cookieValue,
                }
            })
                .then(response => {
                    if (response.data.userid) {
                        setStatus(response.status);
                        setIsInitialLoad(false);
                        checkPaymentStatus();
                    }
                })
                .catch(error => {
                    setStatus(error.response.status);
                    setIsInitialLoad(false);
                });
        } else {
            setStatus(401);
            setShowGoogleLogin(true);
            setIsInitialLoad(false);
        }
    };

    const checkPaymentStatus = async () => {
        try {
            setIsCheckingPayment(true);
            const token = getCookie(`${brandname}_token`);
            if (!token) {
                setError('No authentication token found');
                setIsCheckingPayment(false);
                return;
            }

            const response = await axios.get(`${resource_api_url}/api/CheckPayment`, {
                headers: {
                    Authorization: token,
                }
            });

            setPaymentStatus(response.data);
            setIsCheckingPayment(false);
            // Payment status response received
        } catch (err) {
            // Error checking payment status
            setIsCheckingPayment(false);
            if (err.response && err.response.status === 401) {
                setStatus(401);
                setShowGoogleLogin(true);
            } else {
                setError('Failed to check payment status');
            }
        }
    };

    const createPayment = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = getCookie(`${brandname}_token`);
            if (!token) {
                setError('No authentication token found');
                return;
            }

            const response = await axios.post(`${resource_api_url}/api/CreatePayment`, {
                amount: '1.00',
                description: 'Lifetime Premium Access (Temporary Price)'
            }, {
                headers: {
                    Authorization: token,
                }
            });

            // Redirect to Mollie checkout
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            }
        } catch (err) {
            // Error creating payment
            if (err.response && err.response.status === 401) {
                setStatus(401);
                setShowGoogleLogin(true);
            } else {
                setError('Failed to create payment');
            }
        } finally {
            setLoading(false);
        }
    };



    const onClickHandler = () => {
        setError('After logging into Google, please wait a few seconds for this page to load...');
    };

    // iPhone-specific fix for Google OAuth
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Add iPhone-specific event listeners after component mounts
    useEffect(() => {
        if (isIOS && showGoogleLogin) {
            // Force Google OAuth iframe to be interactive on iOS
            const googleWrapper = document.querySelector('.google-login-wrapper');
            if (googleWrapper) {
                const iframes = googleWrapper.querySelectorAll('iframe');
                iframes.forEach(iframe => {
                    iframe.style.pointerEvents = 'auto';
                    iframe.style.touchAction = 'manipulation';
                    iframe.style.webkitTouchCallout = 'none';
                    iframe.style.webkitUserSelect = 'none';
                    iframe.style.userSelect = 'none';
                    iframe.style.webkitTapHighlightColor = 'transparent';
                    iframe.style.cursor = 'pointer';
                    iframe.style.webkitTransform = 'translateZ(0)';
                    iframe.style.transform = 'translateZ(0)';
                    iframe.style.webkitBackfaceVisibility = 'hidden';
                    iframe.style.backfaceVisibility = 'hidden';
                });
            }
        }
    }, [showGoogleLogin, isIOS]);

    const googleLoginButton = useMemo(() => {
        return (
            <GoogleLogin
                scope="openid email profile"
                shape='pill'
                click_listener={() => onClickHandler()}
                onSuccess={credentialResponse => {
                    // Redirecting to login
                    axios.post(`${login_api_url}/api/Login`, {
                        test: 'test'
                    }, {
                        headers: {
                            Authorization: credentialResponse.credential,
                        }
                    })
                        .then((res) => {
                            setCookie(`${brandname}_token`, res.data.token, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) });
                            setShowGoogleLogin(false);
                            setError(null);
                            setIsInitialLoad(true);
                            checkToken();
                        })
                        .catch((error) => {
                            // Error occurred
                            setError('Google login failed. Please try again.');
                        });
                }}
                onError={() => {
                    // Login failed
                    setError('Google login failed. Please try again.');
                }}
            />
        );
    }, []);

    // Handle different status cases
    useEffect(() => {
        switch (status) {
            case 401:
                setError('Welcome, this is a first-time visit. Please log in using Google');
                setShowGoogleLogin(true);
                break;
            case 440:
                setError('Session Expired, please log in again');
                setShowGoogleLogin(true);
                break;
            default:
                break;
        }
    }, [status]);

    // Show loading status while collecting payment information
    if (isInitialLoad || isCheckingPayment || (status === 200 && paymentStatus === null && !error)) {
        return (
            <div className="content">
                <div className="alert alert-info">
                    <h4><i className="fas fa-spinner fa-spin mr-2"></i>Getting Latest Premium Status</h4>
                    <p>Please wait while we retrieve your latest payment and premium status...</p>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Show Google login if needed
    if (showGoogleLogin) {
        return (
            <div className="content">
                <div className="alert alert-info">
                    <h4>Authentication Required</h4>
                    <p>{error}</p>
                    <br />
                    <div className="google-login-wrapper">{googleLoginButton}</div>
                </div>
            </div>
        );
    }

    if (error && !showGoogleLogin) {
        return (
            <div className="content">
                <div className="alert alert-danger">
                    <h4>Error</h4>
                    <p>{error}</p>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (paymentStatus?.has_paid) {
        return (
            <div className="content">
                {/* Payment Status Summary Box */}
                <div className="card mb-4">
                    <div className="card-header bg-success text-white">
                        <h4 className="mb-0">
                            <i className="fas fa-check-circle mr-2"></i>
                            Premium Access Active
                        </h4>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <p><strong>Status:</strong> <span className="badge badge-success">Active</span></p>
                                <p><strong>Amount Paid:</strong> €{paymentStatus.amount}</p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Access Type:</strong> Lifetime Premium</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="premium-upgrade-section">
                    <div className="premium-title">
                        <i className="fas fa-crown"></i>
                        Welcome to Premium!
                    </div>
                    <div className="premium-description">
                        <p>🎉 Congratulations! Your lifetime premium access is now active!</p>
                        <p>You now have unlimited access to all premium features:</p>
                        
                        <div className="premium-features">
                            <div className="premium-feature">
                                <span className="premium-badge events">
                                    <i className="fas fa-infinity"></i>
                                    Unlimited Events
                                </span>
                            </div>
                            <div className="premium-feature">
                                <span className="premium-badge tracks">
                                    <i className="fas fa-music"></i>
                                    All Track Matches
                                </span>
                            </div>
                            <div className="premium-feature">
                                <span className="premium-badge artists">
                                    <i className="fas fa-users"></i>
                                    Complete Artist Data
                                </span>
                            </div>
                        </div>
                        
                        <p>Amount paid: <strong>€{paymentStatus.amount}</strong></p>
                    </div>
                    
                    <button 
                        className="premium-button"
                        onClick={() => window.location.href = '/admin/getactivities'}
                    >
                        <i className="fas fa-calendar-alt"></i>
                        Start Using Your Premium Features
                    </button>
                </div>
            </div>
        );
    }

    // Show detailed payment failure information if available
    if (paymentStatus?.payment_status && paymentStatus.payment_status !== 'no_payment_record') {
        return (
            <div className="content">
                {/* Payment Status Summary Box */}
                <div className="card mb-4">
                    <div className="card-header bg-danger text-white">
                        <h4 className="mb-0">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Payment Issue
                        </h4>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <p><strong>Status:</strong> <span className="badge badge-danger">{paymentStatus.payment_status.toUpperCase()}</span></p>
                                <p><strong>Payment ID:</strong> {paymentStatus.payment_id || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Amount:</strong> €{paymentStatus.amount || 'N/A'}</p>
                                <p><strong>Created:</strong> {paymentStatus.timestamp ? new Date(paymentStatus.timestamp).toLocaleString() : 'N/A'}</p>
                            </div>
                        </div>
                        {paymentStatus.is_expired && (
                            <div className="alert alert-danger mt-3">
                                <strong>Note:</strong> Your previous payment has expired
                            </div>
                        )}
                    </div>
                </div>

                <div className="alert alert-warning">
                    <h4>Payment Status: {paymentStatus.payment_status.toUpperCase()}</h4>
                    <p>{paymentStatus.status_details}</p>
                    {paymentStatus.payment_id && (
                        <p><strong>Payment ID:</strong> {paymentStatus.payment_id}</p>
                    )}
                    {paymentStatus.amount && (
                        <p><strong>Amount:</strong> €{paymentStatus.amount}</p>
                    )}
                    {paymentStatus.timestamp && (
                        <p><strong>Created:</strong> {new Date(paymentStatus.timestamp).toLocaleString()}</p>
                    )}
                    {paymentStatus.is_expired && (
                        <p className="text-danger"><strong>Note:</strong> Your previous payment has expired</p>
                    )}
                    <hr />
                    <p>You can try creating a new payment below:</p>
                </div>
                
                <div className="card">
                    <div className="card-header">
                        <h4>Lifetime Premium Access</h4>
                    </div>
                    <div className="card-body">
                        <h5>Features included:</h5>
                        <ul>
                            <li>Unlimited event matching</li>
                            <li>Access to all your track matches</li>
                            <li>Complete artist and track data</li>
                            <li>Lifetime access - no recurring fees</li>
                        </ul>
                        
                        <div className="pricing">
                            <h3>€0.50 <span className="badge badge-warning">Temporary Price</span></h3>
                            <p>One-time payment for lifetime access</p>
                        </div>

                        <button 
                            className="btn btn-primary btn-lg"
                            onClick={createPayment}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Try Again'}
                        </button>

                        <div className="mt-3">
                            <small className="text-muted">
                                Payment processed securely by Mollie
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content">
            {/* Payment Status Summary Box */}
            <div className="card mb-4">
                <div className="card-header bg-warning text-dark">
                    <h4 className="mb-0">
                        <i className="fas fa-info-circle mr-2"></i>
                        Payment Status
                    </h4>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <p><strong>Status:</strong> <span className="badge badge-warning">No Payment</span></p>
                            <p><strong>Access Level:</strong> Free</p>
                        </div>
                        <div className="col-md-6">
                            <p><strong>Premium Features:</strong> Not Available</p>
                            <p><strong>Payment Required:</strong> €0.50 <span className="badge badge-warning">Temporary Price</span></p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h4>Lifetime Premium Access</h4>
                </div>
                <div className="card-body">
                    <h5>Features included:</h5>
                    <ul>
                        <li>Unlimited event matching</li>
                        <li>Access to all your track matches</li>
                        <li>Complete artist and track data</li>
                        <li>Lifetime access - no recurring fees</li>
                    </ul>
                    
                    <div className="pricing">
                        <h3>€1.00 <span className="badge badge-warning">Temporary Price</span></h3>
                        <p>One-time payment for lifetime access</p>
                    </div>

                    <button 
                        className="btn btn-primary btn-lg"
                        onClick={createPayment}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Get Lifetime Access'}
                    </button>

                    <div className="mt-3">
                        <small className="text-muted">
                            Payment processed securely by Mollie
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Payment;
